<template>
  <div>
    <SectionSeparatorWrapper headerText="Children" :separator="false" headerRequiredFieldsText="indicates required field">
      <!--Existing child/children on policy-->
      <div v-for="(child, idx) in children.existingChildren" :key="idx" class="existing-child">
        <SectionSeparatorWrapper :headerText="`${referenceData.additionalInformationChildHeading.informationText} ${parseInt(idx) + 1}`" subHeading>
          <FormField :helperText="referenceData.additionalInformationUpdateDetails.informationText" :noLabelOverride="true" :required="false">
            <MemberTag :firstName="child.firstName" :lastName="child.lastName" :showLastName="true" />
          </FormField>
          <!-- keep on policy? -->
          <div class="form-section">
            <FormField :labelText="referenceData.additionalInformationKeepChild.label">
              <ul class="radio-group large">
                <li v-for="option in boolOptions" :key="option.returnValue">
                  <input :id="`keep-on-policy-${idx}-${option.returnValue}`" :value="option.returnValue" type="radio" v-model="child.keepOnPolicy" />
                  <label :for="`keep-on-policy-${idx}-${option.returnValue}`">{{ option.display }}</label>
                </li>
              </ul>
            </FormField>
          </div>

          <!-- If child keep private is off -->
          <template v-if="!doesThisChildHaveKeepPrivateOn(child.personCode)">
            <div class="covered" v-if="child.keepOnPolicy && withinStudentAge(child.DOB) && compositionRequiresStudentDeclaration">
              <!--Child Full-time student?-->
              <div class="form-section">
                <FormField labelText="Is this child a full-time student and single ">
                  <ul class="radio-group large">
                    <li v-for="option in boolOptions" :key="`is-student-${option.display}`">
                      <input
                        type="radio"
                        :id="`is-student-${idx}-${option.display}`"
                        v-model.lazy="child.isStudent"
                        :name="`is-student-${idx}-${option.display}`"
                        :value="option.returnValue"
                      />
                      <label :for="`is-student-${idx}-${option.display}`">{{ option.display }}</label>
                    </li>
                  </ul>
                </FormField>
              </div>

              <!--Student details: uni name, year of study, student declaration-->
              <div class="form-section" v-if="child.isStudent">
                <FormField
                  labelText="Name of the school, college, university or TAFE at which you are attending"
                  placeholder="e.g. DHL University"
                  type="text"
                  required
                  v-model="child.university"
                  :error="v$.children.existingChildren.$each.$response?.$errors?.[idx]?.university?.length > 0"
                  :errorMessage="getInstituteNameError(v$.children.existingChildren.$each.$response?.$errors?.[idx]?.university || [])"
                  @input="child.declaration = false"
                  :id="`existing-child-${idx}-university`"
                />
                <!--year of study-->
                <div>
                  <p>Year of study</p>
                  <p>{{ child.yearOfStudy }}</p>
                </div>
                <!-- Declaration -->
                <FormField>
                  <input type="checkbox" :id="`existing-child-${idx}-declaration`" v-model="child.declaration" />
                  <label :for="`existing-child-${idx}-declaration`">
                    I declare the details that I have provided are true and correct.
                    <span class="required-input">*</span>
                  </label>
                </FormField>
              </div>

              <!-- Not a student anymore -->
              <div class="form-section" v-if="child.isStudent !== null && !child.isStudent && isFamilyCover">
                <div class="form-field">
                  <div class="slotted-items">
                    <Alert
                      message="The product selected only covers dependents and eligible students. This dependent does not meet the criteria. To proceed, the details of this dependent will either need to be edited or removed. Removing this dependent may require you to change your product."
                      alertType="error"
                    />
                  </div>
                </div>
              </div>
            </div>
          </template>

          <!-- Child has keep private on - inline informative message -->
          <div class="form-section" v-else>
            <div class="form-field">
              <div class="slotted-items">
                <Alert
                  message="The child/dependent on this policy has the ‘keep private’ flag enabled, you are not able to view/update their additional information. Your child/dependent will be required to contact Defence Health to update their member details."
                />
              </div>
            </div>
          </div>

          <div class="no-longer-covered" v-if="!child.keepOnPolicy || !under31YearsOld(child.DOB)">
            <FormField>
              <Alert message="This child will no longer be covered on this policy." alertType="info" />
              <Alert
                v-if="!under31YearsOld(child.DOB)"
                message="The product selected covers dependants up to 31 years of age. Once they turn 31, they will need to take out their own cover."
                alertType="error"
              />
              <Alert
                v-if="!hasAtleastOneChildOnPolicy(children)"
                alertType="error"
                :message="'You are unable to proceed with removing this child on your current policy without replacing them. '"
              />
            </FormField>

            <!--Reason for termination-->
            <FormField labelText="Reason for termination" errorMessage="Please include a response for Reason for policy change.">
              <DropdownSelect
                :items="getMemberTerminationReasons"
                v-model="child.terminationReason"
                :initialSelect="getDropdownOption(getMemberTerminationReasons, child.terminationReason, 'Please select a reason')"
                :noDisabled="false"
                :id="`termination-reason-${idx}`"
              />
            </FormField>
            <!--New fund name-->
            <FormField labelText="New fund name" errorMessage="Please include a response for New fund name." v-if="isFundNameRequired(child.terminationReason)">
              <DropdownSelect
                :items="fundNamesTerminated"
                v-model="child.newFundName"
                :initialSelect="getDropdownOption(fundNamesTerminated, child.newFundName, 'Please select a fund name')"
                :noDisabled="false"
                :id="`fund-name-${idx}`"
              />
            </FormField>
          </div>
        </SectionSeparatorWrapper>
      </div>

      <!-- New children on policy -->
      <div v-for="(child, idx) in children.newChildren" :key="children.existingChildren.length + parseInt(idx)" class="new-child">
        <SectionSeparatorWrapper
          :headerText="`${referenceData.additionalInformationChildHeading.informationText} ${children.existingChildren.length + 1 + parseInt(idx)}`"
          :lastSection="false"
          subHeading
        >
          <div v-if="apiStatus.duplicatePersonCheck.loading" class="loading-screen">
            <div class="form-grid">
              <Spinner></Spinner>
            </div>
          </div>
          <!-- First name -->
          <div class="form-section">
            <FormField
              :id="`new-child-first-name-${idx}`"
              labelText="First name"
              placeholder="e.g. John"
              type="text"
              v-model.lazy="child.firstName"
              :error="v$.children.newChildren.$each.$response?.$errors?.[idx]?.firstName?.length > 0"
              :errorMessage="getNameErrorMessage(idx, 'firstName')"
              :disabled="children.newChildren[idx].isDuplicate || apiStatus.duplicatePersonCheck.loading"
              @input="searchDebounce(runDuplicatePersonSearch, idx)"
              @blur="runDuplicatePersonSearch(idx)"
              required
            />
          </div>
          <!-- Middle name -->
          <div class="form-section">
            <FormField
              :id="`new-child-middle-name-${idx}`"
              labelText="Middle name"
              placeholder="e.g. Fred"
              type="text"
              v-model.lazy="child.middleName"
              :disabled="children.newChildren[idx].isDuplicate || apiStatus.duplicatePersonCheck.loading"
              :error="v$.children.newChildren.$each.$response?.$errors?.[idx]?.middleName?.length > 0"
              :errorMessage="getNameErrorMessage(idx, 'middleName')"
              :required="false"
            />
          </div>
          <!-- Last name -->
          <div class="form-section">
            <FormField
              :id="`new-child-last-name-${idx}`"
              labelText="Last name"
              placeholder="e.g. Citizen"
              type="text"
              v-model.lazy="child.lastName"
              :error="v$.children.newChildren.$each.$response?.$errors?.[idx]?.lastName?.length > 0"
              :errorMessage="getNameErrorMessage(idx, 'lastName')"
              :disabled="children.newChildren[idx].isDuplicate || apiStatus.duplicatePersonCheck.loading"
              @input="searchDebounce(runDuplicatePersonSearch, idx)"
              @blur="runDuplicatePersonSearch(idx)"
              required
            />
          </div>
          <!-- Sex -->
          <FormField labelText="Sex" :helperText="informationReferenceData.sex.helpText" required class="form-section">
            <ul class="radio-group large">
              <li v-for="sex in informationReferenceData.sex.options" :key="sex.returnValue">
                <input
                  :id="`${sex.returnValue}${idx}`"
                  :value="sex.returnValue"
                  :disabled="children.newChildren[idx].isDuplicate || apiStatus.duplicatePersonCheck.loading"
                  @change="runDuplicatePersonSearch(idx)"
                  type="radio"
                  v-model="child.sex"
                  :name="`sex + ${idx}`"
                />
                <label :for="`${sex.returnValue}${idx}`">{{ sex.display }}</label>
              </li>
            </ul>
          </FormField>
          <!-- DOB -->
          <FormField labelText="Date of birth" class="form-section half-width" required>
            <FormInput
              placeholder="DD / MM / YYYY"
              v-maska="'## / ## / ####'"
              type="text"
              v-model.lazy.trim="child.DOB"
              maxlength="14"
              :isDateInput="true"
              :id="`new-child-dob-${idx}`"
              :overrideClass="v$.children.newChildren.$each.$response?.$errors?.[idx]?.DOB?.length > 0 ? 'error' : null"
              :disabled="children.newChildren[idx].isDuplicate || apiStatus.duplicatePersonCheck.loading"
              @input="searchDebounce(runDuplicatePersonSearch, idx)"
              @blur="runDuplicatePersonSearch(idx)"
            />
            <label v-if="v$.children.newChildren.$each.$response?.$errors?.[idx]?.DOB?.length > 0" :for="`newChild${idx}`" class="error">
              {{ getDOBErrorMessage(idx) }}
            </label>
            <Alert v-if="turning31In30Days(child.DOB, new Date(changeCoverData.step1.startDate))" class="inline-message">
              <div>
                <p>{{ DOBErrorMessage.IS_ALMOST_31 }}</p>
              </div>
            </Alert>

            <Alert v-if="(v$.children.newChildren.$each.$response?.$errors?.[idx]?.DOB || []).some(e => e.$validator === 'mustBeUnder31YearsOld')" alertType="error" class="inline-message">
              <div>
                <p v-if="!isFamilyPlus">
                  This policy can cover dependants and eligible students who are studying full-time, single and aged between 21 and 30. Whether young adults are working or still
                  studying once they turn 31 it's time for them to take out their own cover.
                </p>
                <p v-else>
                  This policy can cover dependants, eligible students who are studying full-time, single and aged between 21 and 30 and at least one dependant aged between 21 and
                  30 years who is not studying full-time. Whether young adults are working or still studying once they turn 31 it's time for them to take out their own cover.
                </p>

                <p v-if="allChildren.length > 1">
                  <template v-if="!isFamilyPlus">If one of your dependants is aged under 31 you</template>
                  <template v-else>You</template>
                  can continue with this application by removing the dependant aged 31 or above.
                </p>
                <p v-else>
                  You cannot continue with this application. Please
                  <a @click="redirectToChangeCoverStep('MyCover')" class="always-fresh">review and update your details</a>
                  to explore cover options that do not include dependants and we'll help you find the health cover that better meets your need.
                </p>
              </div>
            </Alert>
          </FormField>

          <div class="form-grid">
            <ApiLoading v-if="children.newChildren[idx].duplicateCheckFailed" :apiError="children.newChildren[idx].duplicateCheckFailed">
              <p>
                Sorry, we couldn’t load the information.
                <button @click="runDuplicatePersonSearch(idx)" class="non-button-style">Reload</button>
                the result to continue
              </p>
            </ApiLoading>
          </div>
          <!-- Duplicate alert message-->
          <FormField v-if="children.newChildren[idx].isDuplicate">
            <Alert alertType="error" :message="DUPLICATE_PERSON_ERROR_MESSAGE" />
          </FormField>

          <!-- full time student and single -->
          <div v-if="withinStudentAge(child.DOB) && !isChildOnlyPolicy && !children.newChildren[idx].duplicateCheckFailed && !children.newChildren[idx].isDuplicate">
            <FormField
              labelText="Is this child a full-time student and single?"
              class="form-section"
              :error="(v$.children.newChildren.$each.$response?.$errors?.[idx]?.isStudent || []).some(e => e.$validator === 'required')"
              :errorMessage="`This information is required. Please make a selection.`"
            >
              <ul class="radio-group large">
                <li v-for="option in boolOptions" :key="option.returnValue">
                  <input :id="`isStudent${option.returnValue}${idx}`" :value="option.returnValue" type="radio" v-model.lazy="child.isStudent" :name="`student + ${idx}`" />
                  <label :for="`isStudent${option.returnValue}${idx}`">{{ option.display }}</label>
                </li>
              </ul>

              <Alert v-if="isFamilyPlus && child.isStudent === true && allChildren.length === 1" alertType="error" class="inline-message">
                <div>
                  <p>
                    This policy can cover dependant and eligible students who are studying full-time, single and aged between 21 and 30 and at least one dependant aged between 21
                    and 30 years who is not studying full-time.
                  </p>

                  <p v-if="allChildren.length === 1">
                    Please review your dependant's details to ensure their student status is correct. Alternatively, please
                    <a @click="redirectToChangeCoverStep('MyCover')" class="always-fresh">review and update your dependant details</a>
                    and we'll help you find the health cover that better meets your need.
                  </p>
                </div>
              </Alert>
            </FormField>
            <!-- Not a student on a family or single parent family cover -->
            <div class="form-section" v-if="child.isStudent !== null && !child.isStudent && isFamilyCover">
              <div class="form-field">
                <div class="slotted-items">
                  <Alert
                    message="The product selected only covers dependents and eligible students. This dependent does not meet this criteria. To proceed, the details of this dependent will either need to be edited or removed. Removing this dependent may require a revised needs analysis to be completed."
                    alertType="error"
                  />
                </div>
              </div>
            </div>
            <!-- Name of school, college, uni -->
            <div v-if="child.isStudent" class="form-section">
              <FormField
                labelText="Name of the school, college, university or TAFE at which you are attending"
                placeholder="e.g. DHL University"
                type="text"
                required
                v-model="child.university"
                :error="v$.children.newChildren.$each.$response?.$errors?.[idx]?.university?.length > 0"
                :errorMessage="getInstituteNameError(v$.children.newChildren.$each.$response?.$errors?.[idx]?.university || [])"
              />
              <!--year of study-->
              <div>
                <p>Year of study</p>
                <p>{{ getStudentDeclarationYear(child.studentStartDate) }}</p>
              </div>
              <!-- Declaration -->
              <FormField>
                <input type="checkbox" :id="`new-child-${idx}-declaration`" v-model="child.declaration" />
                <label :for="`new-child-${idx}-declaration`">
                  I declare the details that I have provided are true and correct.
                  <span class="required-input">*</span>
                </label>
              </FormField>
            </div>
          </div>
        </SectionSeparatorWrapper>

        <!-- Transferring from another fund -->
        <SectionSeparatorWrapper
          v-if="!children.newChildren[idx].duplicateCheckFailed && !children.newChildren[idx].isDuplicate"
          headerText="Transferring from another fund"
          :lastSection="children.newChildren.length - 1 === parseInt(idx)"
          :topSeparator="false"
          subHeading
        >
          <div class="form-section">
            <FormField labelText="This person is transferring from another fund">
              <ul class="radio-group large">
                <li v-for="option in boolOptions" :key="`from-other-funds-${option.display}`">
                  <input
                    type="radio"
                    :id="`from-other-funds-${idx}-${option.display}`"
                    v-model.lazy="child.transferFromOthers"
                    :name="`from-other-funds-${idx}-${option.display}`"
                    :value="option.returnValue"
                    :error="(v$.children.newChildren.$each.$response?.$errors?.[idx]?.transferFromOthers || []).some(e => e.$validator === 'required')"
                    @change="!child.transferFromOthers"
                    :errorMessage="`This information is required. Please make a selection.`"
                  />
                  <label :for="`from-other-funds-${idx}-${option.display}`">{{ option.display }}</label>
                </li>
              </ul>
            </FormField>
          </div>
          <template v-if="child.transferFromOthers === true">
            <!-- Previous fund -->
            <div class="form-section" v-for="(fund, index) in child.previousFunds" :key="index">
              <FormField
                labelText="Which fund are you transferring from?"
                class="form-section"
                :error="(v$.children.newChildren.$each.$response?.$errors?.[idx]?.previousFunds?.[0]?.$response?.$errors?.[index]?.fundCode || []).some(e => e.$validator === 'required')"
                :errorMessage="`This information is required. Please make a selection.`"
              >
                <DropdownSelect
                  :items="fundNamesNewPerson"
                  v-model="fund.fundCode"
                  :noDisabled="false"
                  :initialSelect="getDropdownOption(fundNamesNewPerson, fund.fundCode, 'Please select a fund name')"
                />
              </FormField>
              <FormField
                labelText="What was your member number at your previous health fund?"
                placeholder="XXXXXXXXX"
                type="text"
                class="form-section"
                :id="fund.memberNumber"
                v-model.lazy="fund.memberNumber"
                required
                :error="v$.children.newChildren.$each.$response?.$errors?.[idx]?.previousFunds?.[0]?.$response?.$errors?.[index]?.memberNumber?.length > 0"
                :errorMessage="getFundErrorMessage(idx, index)"
                :disabled="false"
              />
              <FormField>
                <p>By providing the details of your previous fund you authorise Defence Health to request cancellation of the products held with your previous health fund.</p>
              </FormField>
              <div v-if="child.previousFunds.length > 1" class="form-section">
                <a class="always-fresh" @click.prevent="removeFund(child.previousFunds, index)" role="button" href="#">Remove fund</a>
              </div>
            </div>
            <div class="form-section">
              <a class="always-fresh" v-if="child.previousFunds.length < 2" @click.prevent="addAnotherFund(child.previousFunds, idx)" role="button" href="#">
                Add another fund to switch from
              </a>
            </div>
          </template>
        </SectionSeparatorWrapper>

        <!-- Remove child link -->
        <div v-if="allChildren.length > 1" class="form-section">
          <a class="always-fresh" id="removeChild" @click.prevent="removeChild(idx)" role="button" href="#">Remove child</a>
        </div>
      </div>

      <!-- Add another child link-->
      <div class="form-section">
        <a
          class="always-fresh"
          id="addAnotherChildButton"
          v-if="children.existingChildren.length + children.newChildren.length < 15"
          @click.prevent="addAnotherChild()"
          role="button"
          href="#"
        >
          {{ referenceData.additionalInformationAddChildButtonLabel.informationText }}
        </a>
      </div>
    </SectionSeparatorWrapper>
  </div>
</template>

<script>
import isEqual from 'lodash.isequal';
import {
  isPastDate,
  under31YearsOld,
  stringWithInvalidSpace,
  stringWithSymbols,
  withinStudentAge,
  isFundNameRequired,
  getStudentDeclarationStartAndEndDate,
  getStudentDeclarationYear,
  formatFormDate,
  isEligibleChild,
  reverseFormatFormDate,
  turning31In30Days,
} from '@general/js/utils';
import { DropdownSelect, Spinner } from '@general/js/components';
import { ApiLoading, FormField, SectionSeparatorWrapper, Alert, FormInput, MemberTag } from '@/components';
import { useVuelidate } from '@vuelidate/core';
import { helpers, maxLength, required, requiredIf } from '@vuelidate/validators';
import { JOIN_NOW_NAME_VALIDATOR, DATE_VALIDATOR } from '@general/js/validators';
import { formatPhoneNumber, getProductName, sortByObjectProperty, isEmptyString } from '@/utils';
import { CoverComposition, DOBErrorMessage, NameErrorMessage } from '@general/js/enums';
import { OperationType } from '@/enums';
import cloneDeep from 'lodash.clonedeep';
import { findDependents } from '@/utils/ChangeProduct/ExtraUtils';
import CaptureInformationMixins from '../CaptureInformationMixins';
import ChangeCoverMixins from '../ChangeCoverMixins';

let triggerDuplicateSearch;
const apiTriggerTime = 1000;

export default {
  setup() { return { v$: useVuelidate() }; },

  name: 'Children',
  mixins: [CaptureInformationMixins, ChangeCoverMixins],

  components: {
    ApiLoading,
    FormField,
    SectionSeparatorWrapper,
    DropdownSelect,
    Alert,
    FormInput,
    Spinner,
    MemberTag,
  },

  props: {
    informationReferenceData: {
      type: Object,
      required: true,
    },
    // Fund names using `dhl_hiccode` when adding a new child
    fundNamesNewPerson: {
      type: Array,
      required: true,
    },
    // Fund names using `dhl_code` when terminating a child
    fundNamesTerminated: {
      type: Array,
      required: true,
    },
    terminationReasons: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      boolOptions: [
        {
          display: 'Yes',
          returnValue: true,
        },
        {
          display: 'No',
          returnValue: false,
        },
      ],
      children: {
        existingChildren: [],
        newChildren: [],
      },
      childrenPayloads: [],
      childrenTerminations: [],
      isActive: false,
      DOBErrorMessage,
    };
  },

  created() {
    this.fetchCurrentChildren();
  },

  beforeMount() {
    if (this.childrenStep && this.childrenStep.newChildren && this.childrenStep.newChildren.length) {
      // Pre-fill data when coming back to this step
      this.children.newChildren = this.childrenStep.newChildren;
    }

    // Add blank forms for new children
    let noOfNewChildren = 0;
    noOfNewChildren = parseInt(this.myCover.numberOfChildren, 10) - (this.children.existingChildren.length + this.children.newChildren.length);
    for (let i = 1; i <= noOfNewChildren; i++) {
      this.addAnotherChild();
    }
  },

  activated() {
    this.isActive = true;
    // This is required to enable the next button if form is already valid upon initial page load.
    if ((this.childrenStep && this.childrenStep.existingChildren.length > 0) || this.children.existingChildren.length > 0) {
      this.v$.$touch();
    }
    this.createChildrenPayload();
    this.$nextTick(() => {
      this.$emit('formUpdated', {
        response: { ...this.children, payloads: this.childrenPayloads, terminations: this.childrenTerminations },
        isInvalid: this.v$.$invalid || this.v$.$error || this.atLeastOneChildIsDuplicate || this.atLeastOneChildDuplicateSearchApiFailed,
        stepResponse: {
          hasIneligibleDependent: this.v$.children.hasIneligibleDependent.$invalid,
          showTopLevelErrorMessage: this.v$.children.hasIneligibleDependent.$invalid,
          atLeastOneNonStudentAndAnother31Plus: this.atLeastOneNonStudentAndAnother31Plus,
          atLeastOneEligibleStudentWithAnotherIneligibleDependant: this.atLeastOneEligibleStudentWithAnotherIneligibleDependant,
          noStudentsNorChildrenAtAll: this.noStudentsNorChildrenAtAll,
          childrenLength: this.children.existingChildren.concat(this.children.newChildren).length,
        },
      });
    });
  },

  deactivated() {
    this.isActive = false;
  },

  validations() {
    return this.validationObj;
  },

  computed: {
    validationObj() {
      const obj = {
        children: {
          hasAtleastOneChildOnPolicy: this.hasAtleastOneChildOnPolicy,
          hasIneligibleDependent: this.hasIneligibleDependentOnPolicy,
          existingChildren: {
            $each: helpers.forEach({
              keepOnPolicy: {
                required,
                isSelected: (value, child) => {
                  // keepOnPolicy needs to be false if child is over 31 years old
                  if (!under31YearsOld(child.DOB)) return !child.keepOnPolicy;
                  // Otherwise pass this validation
                  return true;
                },
              },
              isStudent: {
                required: requiredIf((_value, child) => {
                  // Only required when child is kept on policy, is within student age and composition requires student declaration.
                  return child.keepOnPolicy && withinStudentAge(child.DOB) && this.compositionRequiresStudentDeclaration;
                }),
              },
              university: {
                required: requiredIf((_value, child) => {
                  // only required when child is a student (isStudent = true)
                  return child.keepOnPolicy && !!child.isStudent;
                }),
                JOIN_NOW_NAME_VALIDATOR,
                stringWithInvalidSpace,
                maxLength: maxLength(160),
              },
              declaration: {
                required: requiredIf((_value, child) => {
                  return child.isStudent;
                }),
                isSelected: (value, child) => {
                  // declaration needs to be true if child is kept on policy and a student
                  if (child.keepOnPolicy && child.isStudent) return !!value;
                  // Otherwise pass this validation
                  return true;
                },
              },
              terminationReason: {
                required: requiredIf((_value, child) => {
                  // required only if keepOnPolicy = false
                  return !child.keepOnPolicy || !under31YearsOld(child.DOB);
                }),
              },
              newFundName: {
                required: requiredIf((_value, child) => {
                  // required only for to another fund termination reasons
                  return this.isFundNameRequired(child.terminationReason);
                }),
              },
            }),
          },
          newChildren: {
            $each: helpers.forEach({
              firstName: {
                required,
                JOIN_NOW_NAME_VALIDATOR,
                stringWithInvalidSpace,
                maxLength: maxLength(160),
              },
              middleName: {
                JOIN_NOW_NAME_VALIDATOR,
                stringWithInvalidSpace,
                maxLength: maxLength(160),
              },
              lastName: {
                required,
                JOIN_NOW_NAME_VALIDATOR,
                stringWithInvalidSpace,
                maxLength: maxLength(160),
              },
              sex: {
                required,
              },
              DOB: {
                required,
                DATE_VALIDATOR,
                isPastDate,
                mustBeUnder31YearsOld(value) {
                  return under31YearsOld(value);
                },
              },
              isDuplicate: (currChild) => {
                if (currChild.isDuplicate) {
                  // fail validation by returning false if child is duplicate so that the member cannot proceed with the Change Cover flow
                  return false;
                }
                return true;
              },
              isStudent: {
                required: requiredIf((_value, child) => {
                  return withinStudentAge(child.DOB) && !this.isChildOnlyPolicy; // Student question hidden if child/children only policy
                }),
              },
              university: {
                required: requiredIf((_value, child) => {
                  return child.isStudent;
                }),
                JOIN_NOW_NAME_VALIDATOR,
                stringWithInvalidSpace,
                maxLength: maxLength(160),
              },
              declaration: {
                required: requiredIf((_value, child) => {
                  return child.isStudent;
                }),
                isSelected: (value, child) => {
                  // declaration needs to be true if child is kept on policy and a student
                  if (child.isStudent) return value;
                  // Otherwise pass this validation
                  return true;
                },
              },
              transferFromOthers: {
                required,
              },
              previousFunds: {
                $each: helpers.forEach({
                  fundCode: {
                    required: requiredIf((_value, fund) => {
                      const childObject = this.children.newChildren[fund.childIndex];
                      return childObject?.transferFromOthers === true;
                    }),
                  },
                  memberNumber: {
                    required: requiredIf((_value, fund) => {
                      const childObject = this.children.newChildren[fund.childIndex];
                      return childObject?.transferFromOthers === true;
                    }),
                    stringWithSymbols,
                    maxLength: maxLength(19),
                  },
                }),
              },
            }),
          },
        },
      };
      return obj;
    },

    allChildren() {
      if (this.children.existingChildren && this.children.newChildren) {
        return this.children.existingChildren.concat(this.children.newChildren);
      }
      return [];
    },

    getMemberTerminationReasons() {
      const memberTerminationReasonOptions = [...this.terminationReasons.memberReasons, ...this.terminationReasons.bothReasons];
      return sortByObjectProperty(memberTerminationReasonOptions, 'description').reverse();
    },

    compositionRequiresStudentDeclaration() {
      const currentComposition = this.myCover.composition;
      // Show if family/family+ or single family/single family+
      const requiredFor = [CoverComposition.FAMILY, CoverComposition.FAMILY_PLUS, CoverComposition.SINGLE_PARENT, CoverComposition.SINGLE_PARENT_PLUS];

      if (requiredFor.includes(currentComposition)) {
        return true;
      }

      return false;
    },

    isChildOnlyPolicy() {
      return this.myCover.composition === CoverComposition.CHILDREN_ONLY;
    },

    isFamilyCover() {
      return [CoverComposition.FAMILY, CoverComposition.SINGLE_PARENT].includes(this.selectedProducts.composition);
    },

    isFamilyPlus() {
      return [CoverComposition.FAMILY_PLUS, CoverComposition.SINGLE_PARENT_PLUS].includes(this.selectedProducts.composition);
    },

    atLeastOneNonStudentAndAnother31Plus() {
      const allChildren = this.children.existingChildren.concat(this.children.newChildren);
      let firstNonStudentIndex; // non student means aged 20yrs 11months - 30yrs (incl) AND NOT studying full-time
      for (let i = 0; i < allChildren.length; i++) {
        if (allChildren[i].keepOnPolicy !== false && allChildren[i].isStudent === false && under31YearsOld(reverseFormatFormDate(allChildren[i].DOB))) {
          firstNonStudentIndex = i;
          break;
        }
      }

      const nonFirstNonStudentWith31OrMore = allChildren.filter((child, i) => {
        if (child.keepOnPolicy !== false) {
          return i !== firstNonStudentIndex && !under31YearsOld(reverseFormatFormDate(child.DOB));
        }
        return false;
      });

      return firstNonStudentIndex !== undefined && nonFirstNonStudentWith31OrMore.length > 0;
    },

    atLeastOneEligibleStudentWithAnotherIneligibleDependant() {
      const allChildren = this.children.existingChildren.concat(this.children.newChildren);

      return (
        allChildren.some((child) => {
          const startDate = new Date(this.changeCoverData.step1.startDate);
          if (child.keepOnPolicy === false) {
            return false;
          }
          // check if there are any eligible child or student
          return (child.isStudent === true && withinStudentAge(child.DOB, startDate)) || isEligibleChild(child.DOB, startDate);
        }) &&
        allChildren.some((child) => {
          const startDate = new Date(this.changeCoverData.step1.startDate);
          const childrenFieldInvalid = this.v$.children.newChildren.$invalid;
          if (child.keepOnPolicy === false) {
            return false;
          }
          // check if there are any ineligible dependents
          return (
            (child.isStudent === false && withinStudentAge(child.DOB, startDate) && !childrenFieldInvalid) ||
            (!isEligibleChild(child.DOB, startDate) && !withinStudentAge(child.DOB, startDate) && !childrenFieldInvalid)
          );
        })
      );
    },
    // IF there are no children/eligible students at all, fail validation
    noStudentsNorChildrenAtAll() {
      const allChildren = this.children.existingChildren.concat(this.children.newChildren);
      const startDate = new Date(this.changeCoverData.step1.startDate);
      const childrenFieldInvalid = this.v$.children.newChildren.$invalid;
      if (this.childrenPayloads && this.childrenPayloads.length > 1) {
        return allChildren.every((child) => {
          if (child.keepOnPolicy === false) {
            return true;
          }
          // check if there are no children/student
          return !childrenFieldInvalid && !((child.isStudent === true && withinStudentAge(child.DOB, startDate)) || isEligibleChild(child.DOB, startDate));
        });
      }
      return false;
    },

    atLeastOneChildIsDuplicate() {
      return this.children.newChildren.some((child) => {
        return child.isDuplicate === true;
      });
    },

    atLeastOneChildDuplicateSearchApiFailed() {
      return this.children.newChildren.some((child) => {
        return child.duplicateCheckFailed === true;
      });
    },
  },

  methods: {
    isFundNameRequired,
    under31YearsOld,
    withinStudentAge,
    turning31In30Days,
    getStudentDeclarationYear,
    formatPhoneNumber,
    getProductName,

    searchDebounce(fn, params) {
      clearTimeout(triggerDuplicateSearch);
      triggerDuplicateSearch = setTimeout(async () => {
        await fn(params);
      }, apiTriggerTime);
    },

    async runDuplicatePersonSearch(index) {
      // only run if not already loading
      if (!this.apiStatus.duplicatePersonCheck.loading) {
        const childErrors = this.v$.children.newChildren.$each.$response?.$errors?.[index] || {};
        const firstNameInvalid = childErrors?.firstName?.length > 0;
        const lastNameInvalid = childErrors?.lastName?.length > 0;
        const genderInvalid = childErrors?.sex?.length > 0;
        const dobInvalid = childErrors?.DOB?.length > 0;

        // only run if all four fields are valid
        if (!firstNameInvalid && !lastNameInvalid && !genderInvalid && !dobInvalid) {
          const currentFirstName = this.children.newChildren[index].firstName;
          const currentLastName = this.children.newChildren[index].lastName;
          const currentGender = this.children.newChildren[index].sex;
          const currentDOB = formatFormDate(this.children.newChildren[index].DOB);

          let isPersonDuplicate = false;
          try {
            this.children.newChildren[index].duplicateCheckFailed = false;
            isPersonDuplicate = await this.duplicatePersonSearch(currentFirstName, currentLastName, currentGender, formatFormDate(currentDOB));
            this.children.newChildren[index].isDuplicate = isPersonDuplicate;
          } catch (e) {
            logger.log(e);
            this.children.newChildren[index].duplicateCheckFailed = true;
            this.children.newChildren[index].isDuplicate = false;
          }

          // If duplicate, need to raise a duplicate person work item in CRM
          if (isPersonDuplicate) {
            const payload = {
              firstName: currentFirstName,
              lastName: currentLastName,
              dob: currentDOB,
              sex: currentGender,
              needsAnalysisInfo: { ...this.getNeedAnalysisRecommendationsPayload },
            };
            const request = {
              workItemCategory: 'Duplicate Person Check Member Portal',
              title: 'Duplicate Contact in Change Cover Request',
              payload: { payload },
            };
            // make api call (will need to await if error action is required)
            this.raiseWorkItem(request);
          }
        }
      }
    },

    fetchCurrentChildren() {
      const children = findDependents(this.memberDetails);

      children.forEach((child, index) => {
        const currChildLatestDeclaration = this.getEAIChildCurrentStudentDeclaration(child.personCode);

        const isCurrentYearDeclared = this.isCurrentYearDeclared(child.personCode);

        const initialPostalAddress = {
          addressLine1: child.address.addressLine1,
          addressLine2: child.address.addressLine2,
          addressLine3: child.address.addressLine3,
          suburb: child.address.suburb,
          state: child.address.state,
          postcode: child.address.postcode,
          dpId: child.address.dpId,
        };

        let childObject = {
          keepOnPolicy: true, // default for existing children
          firstName: child.firstName,
          middleName: child.middleName,
          lastName: child.lastName,
          isStudent: isCurrentYearDeclared,
          initialPostalAddress,
          postalAddress: this.policyHolderStep.postalAddress,
          university: isCurrentYearDeclared ? currChildLatestDeclaration.instituteName : null,
          declaration: isCurrentYearDeclared,
          yearOfStudy: getStudentDeclarationYear(new Date()),
          terminationReason: null,
          newFundName: null,
          DOB: reverseFormatFormDate(child.dob),
          // gender: this.convertCodeToValue(this.genderOptions, child.dhl_Member.dhl_selectsex),
          gender: child.sex,
          personCode: child.personCode, // Used to determine if details have changed when creating payload below
          studentStartDate: getStudentDeclarationStartAndEndDate().studentStartDate, // Used to determine if details have changed when creating payload below
          studentEndDate: getStudentDeclarationStartAndEndDate().studentEndDate, // Used to determine if details have changed when creating payload below
        };

        // Pre-fill data when coming back from confirmation step
        if (this.childrenStep && this.childrenStep.existingChildren && this.childrenStep.existingChildren.length > 0 && this.childrenStep.existingChildren[index]) {
          childObject = { ...childObject, ...this.childrenStep.existingChildren[index] };
        }

        this.children.existingChildren.push(childObject);
      });
    },

    isCurrentYearDeclared(personCode) {
      const studentDeclaration = this.getEAIChildCurrentStudentDeclaration(personCode);
      if (studentDeclaration) {
        // if student declaration is in current year then it is declared
        if (getStudentDeclarationYear(new Date(studentDeclaration.startDate)) === getStudentDeclarationYear(new Date())) {
          return true;
        }
        // return null for declarations that occur in the future (requires to re-declare for current year)
        return null;
      }
      return false;
    },

    getEAIChildCurrentStudentDeclaration(personCode) {
      const selectedChild = this.memberDetails.find((member) => {
        return member.personCode === personCode;
      });

      if (selectedChild) {
        if (selectedChild?.studentDeclaration?.length) {
          let latestDeclaration = selectedChild.studentDeclaration.find((d) => {
            // Check if current year is declared. Also if the declaration year is in the future, also takes this declaration.
            return getStudentDeclarationYear(new Date(d.startDate)) >= getStudentDeclarationYear(new Date());
          });
          if (latestDeclaration) {
            // array.find returns a reference for objects, hence need to clone before assigning isStudent as boolean
            latestDeclaration = cloneDeep(latestDeclaration);
            latestDeclaration.isStudent = latestDeclaration.isStudent === true;
            return latestDeclaration;
          }
        }
      }
      return null;
    },

    hasAtleastOneChildOnPolicy(children) {
      const existingChildKeptOnPolicy = children.existingChildren.some((child) => {
        if (child.keepOnPolicy) {
          return under31YearsOld(reverseFormatFormDate(child.DOB));
        }
        return false;
      });

      // check if at least 1 eligible existing child is kept on policy who is also under 31 years
      if (existingChildKeptOnPolicy) return true;

      // OR policy is valid if there's at least 1 new child being added on policy
      if (children.newChildren.length > 0) return true;

      // Otherwise invalid policy
      return false;
    },

    hasIneligibleDependentOnPolicy(children) {
      if ([CoverComposition.FAMILY, CoverComposition.SINGLE_PARENT].includes(this.selectedProducts.composition)) {
        // for existing children
        const invalidChild = children.existingChildren.some((child) => {
          if (!child.keepOnPolicy) {
            return false;
          }
          // child is invalid if they're kept on policy, not a student(within student age) and this is a family cover(hence needs all dependent within student age tohave student declaration)
          if (under31YearsOld(reverseFormatFormDate(child.DOB))) {
            if (withinStudentAge(reverseFormatFormDate(child.DOB)) && child.keepOnPolicy) {
              return !child.isStudent;
            }
            return false;
          }

          return true;
        });

        // for new children
        const invalidNewChild = children.newChildren.some((child) => {
          // child is invalid if not a student(within student age) and this is a family cover
          if (under31YearsOld(reverseFormatFormDate(child.DOB))) {
            if (withinStudentAge(reverseFormatFormDate(child.DOB))) {
              return !child.isStudent;
            }
            return false;
          }
          return true;
        });

        // return false (fail validation) if there exists an invalid child on policy(true), hence the inversion
        return !(invalidChild || invalidNewChild);
      }
      if ([CoverComposition.FAMILY_PLUS, CoverComposition.SINGLE_PARENT_PLUS].includes(this.selectedProducts.composition)) {
        const validChild = children.existingChildren.some((child) => {
          // valid if child is not a student(within student age) and this is a 'plus' cover
          if (child.isStudent === null) {
            return false;
          }
          // if one of the children is within student age and is not a student on the policy, then this is valid
          if (withinStudentAge(reverseFormatFormDate(child.DOB))) {
            return !child.isStudent && child.keepOnPolicy;
          }
          return false;
        });

        const validNewChild = children.newChildren.some((child) => {
          // if child has not been fully filled out, do not show error
          if (this.v$.children.newChildren.$invalid) {
            return true;
          }
          // if student has not been filled in (child may not be within student age)
          if (child.isStudent === null) {
            return false;
          }
          // valid if child is not a student(within student age) and this is a 'plus' cover
          return withinStudentAge(reverseFormatFormDate(child.DOB)) && !child.isStudent;
        });

        // one of existing or new children has a dependent who is within student age and not a student
        return validChild || validNewChild;
      }

      // return true since children are not ineligible
      return true;
    },

    addAnotherChild() {
      const newChild = {
        firstName: '',
        middleName: '',
        lastName: '',
        sex: '',
        DOB: '',
        postalAddress: this.policyHolderStep.postalAddress,
        isStudent: null,
        university: '',
        transferFromOthers: null,
        previousFunds: [{ fundCode: '', memberNumber: '', childIndex: this.children.newChildren.length }],
        studentStartDate: getStudentDeclarationStartAndEndDate().studentStartDate,
        studentEndDate: getStudentDeclarationStartAndEndDate().studentEndDate,
        declaration: false,
        isDuplicate: false,
        duplicateCheckFailed: false,
      };
      this.children.newChildren.push(newChild);
    },

    addAnotherFund(funds, newChildIndex) {
      const newFund = { fundCode: '', memberNumber: '', childIndex: newChildIndex };
      funds.push(newFund);
    },

    removeFund(funds, idx) {
      funds.splice(idx, 1);
    },

    removeChild(idx) {
      this.children.newChildren.splice(idx, 1);
      this.modalIndex = 0; // reset modal index if child gets removed since modal no longer exists

      // Update childIndex for all new children based on new index post removal of child
      this.children.newChildren.forEach((child, index) => {
        child.previousFunds.forEach((fund) => {
          fund.childIndex = index;
        });
      });
      this.v$.children.$touch();
    },

    getNameErrorMessage(index, keyName) {
      const errors = this.v$.children.newChildren.$each.$response?.$errors?.[index]?.[keyName] || [];
      if (keyName !== 'middleName') {
        if (errors.some(e => e.$validator === 'maxLength')) {
          return NameErrorMessage.IS_OVER_LENGTH;
        }
        if (errors.some(e => e.$validator === 'JOIN_NOW_NAME_VALIDATOR' || e.$validator === 'stringWithInvalidSpace')) {
          return NameErrorMessage.IS_INVALID;
        }
        if (errors.some(e => e.$validator === 'required')) {
          if (keyName === 'firstName') return `${NameErrorMessage.IS_EMPTY} First name.`;
          return `${NameErrorMessage.IS_EMPTY} Last name.`;
        }

        return '';
      }
      if (errors.some(e => e.$validator === 'maxLength')) {
        return NameErrorMessage.IS_OVER_LENGTH;
      }
      if (errors.some(e => e.$validator === 'JOIN_NOW_NAME_VALIDATOR' || e.$validator === 'stringWithInvalidSpace')) {
        return NameErrorMessage.IS_INVALID;
      }
      return '';
    },

    getDOBErrorMessage(index) {
      const errors = this.v$.children.newChildren.$each.$response?.$errors?.[index]?.DOB || [];
      if (errors.some(e => e.$validator === 'required')) {
        return `${DOBErrorMessage.IS_EMPTY} Date of birth.`;
      }
      if (errors.some(e => e.$validator === 'isPastDate') && !errors.some(e => e.$validator === 'DATE_VALIDATOR')) {
        return DOBErrorMessage.IS_FUTURE_DATE;
      }
      if (errors.some(e => e.$validator === 'mustBeUnder31YearsOld') && !errors.some(e => e.$validator === 'DATE_VALIDATOR')) {
        return DOBErrorMessage.IS_OVER_31;
      }
      return DOBErrorMessage.IS_INVALID;
    },

    getInstituteNameError(errors) {
      if (errors.some(e => e.$validator === 'maxLength')) {
        return NameErrorMessage.IS_OVER_LENGTH;
      }
      if (errors.some(e => e.$validator === 'JOIN_NOW_NAME_VALIDATOR' || e.$validator === 'stringWithInvalidSpace')) {
        return NameErrorMessage.IS_INVALID;
      }
      if (errors.some(e => e.$validator === 'required')) {
        return `${NameErrorMessage.IS_EMPTY} Institute name.`;
      }
      return '';
    },

    getFundErrorMessage(childIdx, fundIdx) {
      const childErrors = this.v$.children.newChildren.$each.$response?.$errors?.[childIdx];
      const fundErrors = childErrors?.previousFunds?.[0]?.$response?.$errors?.[fundIdx]?.memberNumber || [];
      if (fundErrors.some(e => e.$validator === 'required')) return 'Please include a response for your member number.';
      if (fundErrors.some(e => e.$validator === 'maxLength' || e.$validator === 'stringWithSymbols')) return 'Please enter a combination of up to 19 letters or numbers.';
      return '';
    },

    isStudentDeclarationChanged(oldDeclaration, newDeclaration) {
      return !(
        oldDeclaration?.isStudent === newDeclaration.isStudent &&
        oldDeclaration?.instituteName === newDeclaration.instituteName &&
        oldDeclaration?.startDate === newDeclaration.startDate &&
        oldDeclaration?.endDate === newDeclaration.endDate
      );
    },

    createChildrenPayload() {
      this.childrenPayloads = [];
      this.childrenTerminations = [];
      // add to this.children.payload
      this.children.existingChildren.forEach((child) => {
        const payload = {};
        payload.personCode = child.personCode;
        payload.dateOfBirth = child.DOB;

        if (child.keepOnPolicy === false) {
          payload.terminationReason = child.terminationReason;
          if (child.newFundName) {
            payload.fundName = child.newFundName;
          }
          this.childrenTerminations.push(payload);
        } else {
          this.childrenPayloads.push(this.createExistingChildPayload(child, payload));
        }
      });

      // Add new child payload
      this.children.newChildren.forEach((child) => {
        const payload = {};
        payload.op = OperationType.ADD;
        payload.firstName = child.firstName;
        if (!isEmptyString(child.middleName)) payload.middleName = child.middleName;
        payload.lastName = child.lastName;
        payload.gender = child.sex;
        payload.dateOfBirth = formatFormDate(child.DOB);

        // need to use clonedeep otherwise it returns a reference to object and modifying values modifies the original object
        payload.previousFunds = cloneDeep(
          child.previousFunds.map((f) => {
            return {
              fundCode: f.fundCode.split('-')[0],
              memberNumber: f.memberNumber,
              isAutomaticTransfer: this.isFundAutomaticTransfer(f.fundCode),
            };
          }),
        );
        payload.transferFromOthers = child.transferFromOthers;
        payload.isStudent = !!child.isStudent;
        payload.postalAddress = {
          op: OperationType.ADD,
          ...child.postalAddress,
        };

        if (withinStudentAge(child.DOB) && child.isStudent && !this.isChildOnlyPolicy) {
          payload.studentDeclaration = {
            op: OperationType.ADD,
            startDate: child.studentStartDate,
            endDate: child.studentEndDate,
            instituteName: child.university,
          };
        } else {
          payload.studentDeclaration = null;
        }

        this.childrenPayloads.push(payload);
      });
    },

    createExistingChildPayload(child, payload) {
      // Postal address payload
      const initialPostal = child.initialPostalAddress;
      const currentPostal = child.postalAddress;

      let postalOp = OperationType.NONE;
      if (initialPostal?.addressLine1 && !isEqual(initialPostal, currentPostal)) {
        postalOp = OperationType.REPLACE;
      } else if (!initialPostal?.addressLine1) {
        postalOp = OperationType.ADD;
      }

      payload.postalAddress = {
        op: postalOp,
        ...currentPostal,
      };

      payload.isStudent = child.isStudent ?? null;

      // Student declaration payload
      const currentDeclaration = this.getEAIChildCurrentStudentDeclaration(child.personCode);

      const newDeclaration = {
        isStudent: child.isStudent ?? null,
        instituteName: child.university,
        startDate: child.studentStartDate,
        endDate: child.studentEndDate,
      };

      if (this.isCurrentYearDeclared(child.personCode)) {
        // Current declaration exists
        payload.op = OperationType.REPLACE;

        if (newDeclaration.isStudent && this.isStudentDeclarationChanged(currentDeclaration, newDeclaration)) {
          // Current declaration exists, and it is changed, and child is a student, update declaration (REPLACE)
          payload.isStudent = newDeclaration.isStudent;
          payload.studentDeclaration = newDeclaration;
          payload.studentDeclaration.op = OperationType.REPLACE;
          delete payload.studentDeclaration.isStudent; // remove this key as it's added outside the declaration object
        } else if (!newDeclaration.isStudent) {
          // Current declaration exists, but child is not a student anymore, remove declaration
          payload.isStudent = newDeclaration.isStudent;
          payload.studentDeclaration = {
            op: OperationType.REMOVE,
            id: currentDeclaration.id,
          };
        } else {
          // Current declaration exists, hasn't changed and child is still a student, do nothing to declaration
          payload.op = postalOp === OperationType.NONE ? OperationType.NONE : OperationType.REPLACE;
        }
      } else if (!this.isCurrentYearDeclared(child.personCode) && newDeclaration.isStudent) {
        // Just a note here that even if a declaration in the future exists, will always require a new declaration for this current student year.
        // This will always be an ADD since they would be declaring for current year
        payload.op = OperationType.REPLACE;
        payload.isStudent = newDeclaration.isStudent;
        // Add declaration
        payload.studentDeclaration = {
          op: OperationType.ADD,
          startDate: newDeclaration.startDate,
          endDate: newDeclaration.endDate,
          instituteName: newDeclaration.instituteName,
        };
      } else if (!this.isStudentDeclarationChanged(currentDeclaration, newDeclaration) || (!this.isCurrentYearDeclared(child.personCode) && !newDeclaration.isStudent)) {
        // Declaration hasn't changed OR (No declaration exists and child is not a student), do nothing to the declaration
        payload.op = postalOp === OperationType.NONE ? OperationType.NONE : OperationType.REPLACE;
      }

      return payload;
    },
  },

  watch: {
    children: {
      handler() {
        this.createChildrenPayload();
        if (this.isActive) {
          this.$nextTick(() => {
            this.$emit('formUpdated', {
              response: { ...this.children, payloads: this.childrenPayloads, terminations: this.childrenTerminations },
              isInvalid: this.v$.$invalid || this.v$.$error || this.atLeastOneChildIsDuplicate || this.atLeastOneChildDuplicateSearchApiFailed,
              stepResponse: {
                hasIneligibleDependent: this.v$.children.hasIneligibleDependent.$invalid,
                showTopLevelErrorMessage: this.v$.children.hasIneligibleDependent.$invalid,
                atLeastOneNonStudentAndAnother31Plus: this.atLeastOneNonStudentAndAnother31Plus,
                atLeastOneEligibleStudentWithAnotherIneligibleDependant: this.atLeastOneEligibleStudentWithAnotherIneligibleDependant,
                noStudentsNorChildrenAtAll: this.noStudentsNorChildrenAtAll,
                childrenLength: this.children.existingChildren.concat(this.children.newChildren).length,
              },
            });
          });
        }
      },
      deep: true,
    },
    'apiStatus.duplicatePersonCheck.loading': {
      handler() {
        this.$emit('apiLoading', { apiLoading: this.apiStatus.duplicatePersonCheck.loading });
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.name-row {
  display: flex;
  .name-col {
    flex-basis: 100%;
    flex: 1;
  }
}

.form-field {
  @include grid-layout(null, spacer(1));
  @include grid-template(2fr 1fr);
  row-gap: 0;
  margin-bottom: spacer(2);

  .slotted-items {
    @include grid-position(1, 1, null, 2);

    &.with-label {
      @include grid-position(1, 2);
    }
  }
}

.bottom-margin {
  margin-bottom: 10px;
}

.loading-screen {
  position: absolute;
  background: $WHITE;
  backdrop-filter: none;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 999999;
  opacity: 1;
}

.form-grid {
  display: grid;
  top: 50%;
  grid-template-columns: 2fr 1fr;
  align-items: center;
  @include desktop {
    column-gap: $desktop_spacing;
  }

  @include tablet {
    column-gap: $tablet_spacing;
  }

  @include mobile {
    column-gap: $mobile_spacing;
    grid-template-columns: 100%;
    grid-template-rows: auto;
  }
}

:deep(#spinner) {
  text-align: center;
}

:deep(.spinner) {
  div {
    &::after {
      top: 48px;
      left: 38px;
      width: 4px;
      height: 22px;
    }
  }
}

:deep(.modal-table) {
  width: 100%;
  margin-bottom: 0;
  text-align: right;

  tr {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  th,
  td {
    border-bottom: none;
    padding-left: 0;
  }

  .break-long-email {
    word-break: break-all;
  }

  a {
    margin-top: 10px;
  }
}
</style>
